/**
 * The company and legal destinations in the footer.
 *
 * `href` is optional, and its absence is the whole mechanism: `FooterLink` renders an entry with
 * an `href` as an anchor and an entry without one as plain, non-interactive text — not focusable,
 * no pointer affordance, navigating nowhere (REQ-LANDING-005). Giving a destination a page is
 * therefore one line here, and nothing else changes.
 *
 * The alternative — `href="#"`, which the design prototype uses — was rejected: those are dead
 * links that jump the page, get indexed, and are flagged by link and accessibility checkers.
 *
 * ── Why the legal paths are plain strings and not routes ────────────────────────────────────
 *
 * `/legal/*` is **not a route in this application**. It is served by `trabeate-content` from its
 * own bucket, through a separate CloudFront behaviour on this same hostname. So these must stay
 * ordinary `<a href>` navigations — a client-side `<Link>` would ask this app's router for a route
 * it does not have. `FooterLink` already renders an anchor, so nothing there needs to change.
 *
 * They are same-origin by construction, which is why they take no `target` or `rel` treatment; the
 * social links are the external ones.
 *
 * ── The paths are a contract with another repository ────────────────────────────────────────
 *
 * Slugs come from filenames in `trabeate-content` (`privacy.md` → `/legal/privacy`), written
 * without a trailing slash to match how that site links its own pages. Renaming a document there
 * breaks these links here, and nothing in either build will say so — the two repositories share no
 * code, and the link checker runs against that site's output, not this one's.
 */

export interface FooterLink {
  readonly label: string;
  /** Omitted while the destination has no content. */
  readonly href?: string;
}

export const COMPANY_LINKS: readonly FooterLink[] = [
  // No content yet — deliberately not links. See the rule above.
  { label: 'About' },
  { label: 'Contact' },

  // Published by `trabeate-content` and reachable since the `/legal/*` behaviour exists.
  { label: 'Privacy Policy', href: '/legal/privacy' },
  { label: 'Terms of Service', href: '/legal/terms' },
  { label: 'Cookie Policy', href: '/legal/cookies' },
  { label: 'Disclaimer', href: '/legal/disclaimer' },
  // Distinct from the `© <year> Trabeate` line this row ends with: that is a notice, this is the
  // document explaining what the notice covers.
  { label: 'Copyright', href: '/legal/copyright' },
];
